import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import * as I from 'immutable';
import React from 'react';

import FilterableList from 'app/components/FilterableList';
import {ListItem} from 'app/components/List';
import {HydratedFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {ApiProject} from 'app/modules/Remote/Project';
import {FeatureSelection} from 'app/pages/ProjectDashboardSwitch';
import {
  TagFilter,
  TagFilterValue,
  featureTagFilter,
} from 'app/pages/PropertyOverview/ColumnFilters';
import * as featureUtils from 'app/utils/featureUtils';
import {useContinuity} from 'app/utils/hookUtils';
import {sortMultiFeaturePropertyPartsCallback} from 'app/utils/multiFeaturePropertyUtils';
import * as routeUtils from 'app/utils/routeUtils';
import * as tagUtils from 'app/utils/tagUtils';

import cs from './styles.module.scss';

type FeatureListItem = ListItem<
  {
    lensIds?: I.Set<string>;
    href?: string;
    // Set on multi-location parent rows: the group name to expand when the row
    // itself is clicked (the chevron action handles toggling separately).
    expandableName?: string;
  },
  string
>;

export type FilterType = {type: 'all'} | {type: 'assigned'} | {type: 'tags'; value: TagFilterValue};

export interface Props {
  selectedFeatureCollection: HydratedFeatureCollection;
  selectedFeatureLensIds: I.Set<string>;
  selectedProject: I.ImmutableOf<ApiProject>;

  // The authenticated user's profile (could be in a parent org)
  profile: I.ImmutableOf<ApiOrganizationUser>;
  // The selected organization (could be a child)
  organization: I.ImmutableOf<ApiOrganization>;

  className?: string;
  listClassName?: string;

  deselectText?: string | null;

  filter: FilterType;
  setFilter: null | ((f: FilterType) => void);

  /** We keep this on FeatureList to make it easier to test without mocking anything out. */
  changeSelection: (selection: FeatureSelection) => void;
  hideBackLink?: boolean;
}

const FeatureList: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  organization,
  profile,
  listClassName,
  className,
  selectedFeatureCollection,
  selectedFeatureLensIds,
  selectedProject,
  hideBackLink,
  changeSelection,
  filter,
  // We keep filter state outside of this component so that it persists across
  // the menu opening.
  setFilter,
}) => {
  // pull these out to ignore other updates to FeatureCollection or Profile or Project
  const features = selectedFeatureCollection.get('features');
  const profileEmail = profile.get('email');
  const selectedProjectId = selectedProject.get('id');

  const tagSettings = tagUtils.getTagSettings(organization, tagUtils.TAG_KIND.FEATURE);

  if (filter.type === 'tags') {
    // Handles tags that are being filtered on getting deleted.
    const activeTagIds = filter.value.tagIds
      .filter((id) => tagSettings.get(id!) && !tagSettings.get(id!).get('isArchived'))
      .toSet();

    if (!I.is(activeTagIds, filter.value.tagIds)) {
      filter = {
        type: 'tags',
        value: {
          op: filter.value.op,
          tagIds: activeTagIds,
        },
      };
    }
  }

  // Keeps us from recalculating items more than necessary when the above
  // activeTagIds check modifies filter.
  filter = useContinuity(filter);

  // Continuity on this since it may be built using map in the caller.
  selectedFeatureLensIds = useContinuity(selectedFeatureLensIds, I.is);

  // On mount, expand any multi-location property that has at least one selected
  // sub-location so the user sees their selection without having to click open.
  const [expandedNames, setExpandedNames] = React.useState<I.Set<string>>(() =>
    features
      .groupBy((f) => f!.getIn(['properties', 'name']))
      .filter(
        (fs) =>
          fs!.size > 1 &&
          fs!.some((f) => selectedFeatureLensIds.has(f!.getIn(['properties', 'lensId'])))
      )
      .keySeq()
      .toSet()
  );

  const toggleExpanded = React.useCallback((name: string) => {
    setExpandedNames((current) => (current.has(name) ? current.remove(name) : current.add(name)));
  }, []);

  // Clicking a multi-location parent row expands it (but never collapses —
  // collapsing stays on the chevron so selecting-all never hides the sub-rows).
  const expandName = React.useCallback((name: string) => {
    setExpandedNames((current) => current.add(name));
  }, []);

  // When selection moves into a multi-location group that wasn't already
  // expanded (e.g. a sub-location is picked from the map while the popover is
  // mounted), reveal it. Additive only — we union into the existing set rather
  // than replace it, so manual collapses on unrelated groups survive.
  React.useEffect(() => {
    setExpandedNames((current) =>
      current.union(
        features
          .groupBy((f) => f!.getIn(['properties', 'name']))
          .filter(
            (fs) =>
              fs!.size > 1 &&
              fs!.some((f) => selectedFeatureLensIds.has(f!.getIn(['properties', 'lensId'])))
          )
          .keySeq()
          .toSet()
      )
    );
  }, [features, selectedFeatureLensIds]);

  const items = React.useMemo<FeatureListItem[]>(() => {
    const groups = features
      .groupBy((f) => f!.getIn(['properties', 'name']))
      .map((fs) => fs!.sortBy((f) => f!.get('id')))
      .filter((fs) => {
        switch (filter.type) {
          case 'all':
            return true;
          case 'assigned':
            return featureUtils.getFeatureAssignees(fs!.first()).includes(profileEmail);
          case 'tags':
            return featureTagFilter(fs!.toList(), filter.value);
        }
      })
      .valueSeq()
      .toArray()
      .sort((a, b) =>
        featureUtils.alphanumericSort(
          a.first().getIn(['properties', 'name']),
          b.first().getIn(['properties', 'name'])
        )
      );

    const result: FeatureListItem[] = [];

    groups.forEach((fs) => {
      const groupFeatures = fs.toList();
      const name: string = fs.first().getIn(['properties', 'name']);
      const lensIds = fs.map((f) => f!.getIn(['properties', 'lensId'])!).toSet();
      const tagIds = tagUtils.getFeaturesTagIds(groupFeatures);
      const tagCounts = tagUtils.getFeaturesTagCounts(groupFeatures);
      const isMultiLocation = groupFeatures.size > 1;
      const isExpanded = isMultiLocation && expandedNames.has(name);

      const rightEl = (
        <>
          {tagSettings
            .filter((s) => tagIds.contains(s!.get('id')))
            .map((s) => (
              <tagUtils.Tag
                key={s!.get('id')}
                setting={s!}
                small
                indeterminate={tagCounts.get(s!.get('id')) !== groupFeatures.size}
              />
            ))
            .toArray()}
        </>
      );

      const selectedSubCount = selectedFeatureLensIds.intersect(lensIds).size;
      // Some (but not all) sub-locations selected — show the parent in an
      // indeterminate-ish state so it's findable when the list is collapsed.
      const hasPartialSubSelection =
        isMultiLocation && selectedSubCount > 0 && selectedSubCount < lensIds.size;

      const parentItem: FeatureListItem = {
        text: name,
        lensIds,
        // Only mark the parent selected when *all* of its lensIds are selected,
        // so clicking a single sub-location highlights only that sub-row.
        isSelected: lensIds.isSubset(selectedFeatureLensIds),
        href: routeUtils.makeProjectDashboardUrl(
          organization,
          selectedProjectId,
          'map',
          lensIds.toArray()
        ),
        rightEl,
        className: classnames({
          [cs.hasSubSelection]: hasPartialSubSelection,
          [cs.expandedParent]: isExpanded,
        }),
      };

      if (isMultiLocation) {
        // Clicking the row (not just the chevron) should reveal the sub-rows.
        parentItem.expandableName = name;

        // Count pill next to the name so the row reads as a group of locations.
        parentItem.textSuffix = (
          <span className={cs.locationCount}>{groupFeatures.size} locations</span>
        );

        // Surface sub-location part names to FilterableList's search.
        parentItem.additionalSearchText = groupFeatures
          .map((f) => f!.getIn(['properties', 'multiFeaturePartName'], '') as string)
          .filter((p) => !!p)
          .join(' ');

        // Caret renders on the right via `actions` (alongside any tags) so
        // single-location rows keep their names flush-left.
        parentItem.actions = [
          {
            icon: isExpanded ? 'chevron-down' : 'chevron-right',
            text: isExpanded ? 'Hide locations' : 'Show locations',
            onClick: () => toggleExpanded(name),
          },
        ];
      }

      result.push(parentItem);

      if (isExpanded) {
        const sortedParts = groupFeatures
          .map((f) => ({
            partName: f!.getIn(['properties', 'multiFeaturePartName']) as string,
            feature: f!,
          }))
          .sort(sortMultiFeaturePropertyPartsCallback)
          .toArray();

        sortedParts.forEach(({partName, feature}) => {
          const lensId = feature.getIn(['properties', 'lensId']) as string;
          // Mirrors the `|| 'Location'` fallback in PropertyTableSharedComponents
          // for sub-rows whose `multiFeaturePartName` is missing.
          const displayName = partName || 'Location';
          const subTagIds = tagUtils.getFeatureTagIds(feature);

          result.push({
            text: displayName,
            icon: <B.Icon icon="key-enter" style={{transform: 'scaleX(-1)'}} />,
            lensIds: I.Set([lensId]),
            isSelected: selectedFeatureLensIds.has(lensId),
            href: routeUtils.makeProjectDashboardUrl(organization, selectedProjectId, 'map', [
              lensId,
            ]),
            className: cs.subRow,
            ariaLabel: `${displayName}, sub-location of ${name}`,
            rightEl: (
              <>
                {tagSettings
                  .filter((s) => subTagIds.contains(s!.get('id')))
                  .map((s) => (
                    <tagUtils.Tag key={s!.get('id')} setting={s!} small />
                  ))
                  .toArray()}
              </>
            ),
          });
        });
      }
    });

    return result;
  }, [
    features,
    selectedFeatureLensIds,
    filter,
    profileEmail,
    selectedProjectId,
    tagSettings,
    organization,
    expandedNames,
    toggleExpanded,
  ]);

  return (
    <div className={cs.container}>
      {(!hideBackLink || setFilter) && (
        <div className={cs.toolbar}>
          <div className={cs.projectNameText}>
            {hideBackLink ? (
              // We put an empty element here so that the filter buttons still get
              // pushed to the left.
              <span>{selectedProject.get('name')}</span>
            ) : (
              <a
                onClick={(ev) => {
                  ev.preventDefault();

                  changeSelection({
                    organizationId: organization.get('id'),
                    projectId: selectedProjectId,
                    featureIds: null,
                  });
                }}
              >
                {`← Back to ${selectedProject.get('name')}`}
              </a>
            )}
          </div>

          {setFilter && (
            <B.ButtonGroup minimal>
              <B.Tooltip content="Show all properties">
                <B.AnchorButton
                  icon="globe"
                  active={
                    filter.type === 'all' ||
                    // Wee hack since w/o tagIds set the tags filter shows "all"
                    (filter.type === 'tags' && filter.value.tagIds.isEmpty())
                  }
                  onClick={() => setFilter({type: 'all'})}
                />
              </B.Tooltip>
              <B.Tooltip content="Show properties assigned to me">
                <B.AnchorButton
                  icon="person"
                  active={filter.type === 'assigned'}
                  onClick={() => setFilter({type: 'assigned'})}
                />
              </B.Tooltip>
              <TagFilter
                tagSettings={tagSettings}
                value={filter.type === 'tags' ? filter.value : {tagIds: I.Set(), op: 'all'}}
                setValue={(value) =>
                  setFilter({type: 'tags', value: value ? value : {tagIds: I.Set(), op: 'all'}})
                }
              />
            </B.ButtonGroup>
          )}
        </div>
      )}
      {items.length === 0 ? (
        <div className={cs.message}>
          {filter.type === 'assigned' && 'There aren’t any properties assigned to you.'}
          {filter.type === 'tags' && 'There aren’t any properties that match the filter.'}
        </div>
      ) : (
        <FilterableList
          items={items}
          // enough space for tags
          itemHeight={32}
          onClickItem={(
            item: FeatureListItem,
            event: React.SyntheticEvent | React.MouseEvent | undefined
          ) => {
            // If the item has an `href` property and the shift or command key
            // is being pressed, open a window instead of changing the current
            // selection.
            //
            // Casting event as `any` here since the @blueprintjs/select
            // `onItemSelect` function is invoked with an `event` parameter
            // typed as a `SyntheticEvent`, which doesn't have `shiftKey` and
            // `metaKey` properties. This may be a mistyping, since the `event`
            // provided to the callback does have those properties.
            if (item.href && event && ((event as any).shiftKey || (event as any).metaKey)) {
              event.stopPropagation();
              window.open(item.href, (event as any).metaKey ? '_blank' : undefined);
              return;
            }

            // Multi-location parent rows expand on click so the user can see
            // their sub-locations without having to hit the chevron.
            if (item.expandableName) {
              expandName(item.expandableName);
            }

            changeSelection({
              organizationId: organization.get('id'),
              projectId: selectedProjectId,
              featureIds: item.lensIds || null,
            });
          }}
          listClassName={listClassName || ''}
          className={className || ''}
        />
      )}
    </div>
  );
};

export default FeatureList;
